import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { initials } from "../lib/format";
import { useAuth } from "../lib/auth";
import { getUnreadMessageCount, getUnreadNotificationCount, touchCurrentUserLastSeen } from "../lib/api";
import { Icon } from "./Icon";

type Props = {
  children: ReactNode;
  route: string;
  go: (route: string, params?: Record<string, string>) => void;
};

function badgeCount(count: number): string {
  return count > 9 ? "9+" : String(count);
}

function isMobileNavActive(route: string, item: "search" | "favorites" | "listings" | "messages" | "profile") {
  if (item === "search") return route === "home" || route === "catalog";
  if (item === "favorites") return route === "dashboard.favorites";
  if (item === "listings") return route === "dashboard" || route === "listing.create" || route === "listing.edit";
  if (item === "messages") return route === "dashboard.messages" || route === "chat";
  return ["dashboard.settings", "dashboard.notifications", "dashboard.resume", "dashboard.moderation"].includes(route);
}

export function Layout({ children, route, go }: Props) {
  const { profile, user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0);
      setUnreadNotificationCount(0);
      return;
    }
    let alive = true;
    const refreshUnreadCounts = () => {
      void Promise.all([
        getUnreadMessageCount(),
        getUnreadNotificationCount(profile?.role === "admin")
      ]).then(([messageCount, notificationCount]) => {
        if (!alive) return;
        setUnreadMessageCount(messageCount);
        setUnreadNotificationCount(notificationCount);
      }).catch((error) => console.warn(error));
    };
    refreshUnreadCounts();
    void touchCurrentUserLastSeen();
    window.addEventListener("premamarket:messages-read", refreshUnreadCounts);
    window.addEventListener("premamarket:notifications-read", refreshUnreadCounts);
    const timer = window.setInterval(() => {
      refreshUnreadCounts();
      void touchCurrentUserLastSeen();
    }, 120000);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener("premamarket:messages-read", refreshUnreadCounts);
      window.removeEventListener("premamarket:notifications-read", refreshUnreadCounts);
    };
  }, [user?.id, profile?.role, route]);

  const logout = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
    go("home");
  };

  const goPrivate = (targetRoute: string, params?: Record<string, string>) => {
    go(user ? targetRoute : "login", user ? params : targetRoute === "dashboard" ? { reason: "listing" } : undefined);
  };

  return (
    <>
      <header className="site-header">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); go("home"); }}>
          <span className="brand-mark"><img src="/assets/logo-lotus.png" alt="" /></span>
          <span>
            <strong><span>Prema</span><em>Market</em></strong>
            <small>Этичный маркетплейс объявлений</small>
          </span>
        </a>
        <div className="header-actions">
          <a className="btn btn-primary" href="/help" onClick={(event) => { event.preventDefault(); go("help"); }}>Помощь</a>
          <button className="btn btn-primary" type="button" onClick={() => go(user ? "listing.new" : "login", user ? undefined : { reason: "listing" })}>+ Разместить объявление</button>
          {user ? (
            <>
              <button className="header-icon-button" type="button" onClick={() => go("dashboard.favorites")} title="Избранное"><Icon name="heart" /></button>
              <button className="header-icon-button" type="button" onClick={() => go("dashboard.messages")} title="Сообщения">
                <Icon name="message" />
                {unreadMessageCount ? <span className="header-badge">{badgeCount(unreadMessageCount)}</span> : null}
              </button>
              <button className="header-icon-button" type="button" onClick={() => go("dashboard.notifications")} title="Уведомления">
                <Icon name="bell" />
                {unreadNotificationCount ? <span className="header-badge">{badgeCount(unreadNotificationCount)}</span> : null}
              </button>
              <div className="avatar-menu" ref={menuRef} onMouseEnter={() => setMenuOpen(true)}>
                <button
                  className="avatar"
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  title="Меню профиля"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : initials(profile?.display_name || user.email)}
                </button>
                {menuOpen ? (
                  <div className="avatar-dropdown" onMouseLeave={() => setMenuOpen(false)}>
                    <div className="avatar-dropdown-head">
                      <span className="avatar">{profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : initials(profile?.display_name || user.email)}</span>
                      <span><b>{profile?.display_name ?? user.email}</b><small>{user.email}</small></span>
                    </div>
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard"); }}><Icon name="grid" />Мои объявления</button>
                    {profile?.role === "admin" ? <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.moderation"); }}><Icon name="briefcase" />Модерация</button> : null}
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.favorites"); }}><Icon name="heart" />Избранное</button>
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.messages"); }}><Icon name="message" />Сообщения</button>
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.notifications"); }}><Icon name="bell" />Уведомления</button>
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.settings"); }}><Icon name="settings" />Настройки</button>
                    <button type="button" onClick={() => { setMenuOpen(false); go("dashboard.resume"); }}><Icon name="user" />Моё резюме</button>
                    <button type="button" onClick={logout}><Icon name="logout" />Выйти</button>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <button className="btn btn-ghost" type="button" onClick={() => go("login")}>Войти</button>
          )}
        </div>
      </header>
      <main data-route={route}>{children}</main>
      <footer className="site-footer">
        <strong>PremaMarket</strong>
        <nav className="site-footer-nav" aria-label="Информация о PremaMarket">
          <a href="/premamarket-online" onClick={(event) => { event.preventDefault(); go("premamarket-online"); }}>О площадке</a>
          <a href="/catalog" onClick={(event) => { event.preventDefault(); go("catalog"); }}>Каталог</a>
          <a href="/help" onClick={(event) => { event.preventDefault(); go("help"); }}>Помощь</a>
          <a href="/legal/terms" onClick={(event) => { event.preventDefault(); go("legal.terms"); }}>Соглашение</a>
          <a href="/legal/privacy" onClick={(event) => { event.preventDefault(); go("legal.privacy"); }}>Конфиденциальность</a>
        </nav>
      </footer>
      <nav className="mobile-bottom-nav" aria-label="Мобильная навигация">
        <button className={isMobileNavActive(route, "search") ? "active" : ""} type="button" onClick={() => go("home")} aria-current={isMobileNavActive(route, "search") ? "page" : undefined}>
          <span className="mobile-nav-icon"><Icon name="search" /></span>
          <span>Поиск</span>
        </button>
        <button className={isMobileNavActive(route, "favorites") ? "active" : ""} type="button" onClick={() => goPrivate("dashboard.favorites")} aria-current={isMobileNavActive(route, "favorites") ? "page" : undefined}>
          <span className="mobile-nav-icon"><Icon name="heart" /></span>
          <span>Избранное</span>
        </button>
        <button className={isMobileNavActive(route, "listings") ? "active" : ""} type="button" onClick={() => goPrivate("dashboard")} aria-current={isMobileNavActive(route, "listings") ? "page" : undefined}>
          <span className="mobile-nav-icon"><Icon name="grid" /></span>
          <span>Объявления</span>
        </button>
        <button className={isMobileNavActive(route, "messages") ? "active" : ""} type="button" onClick={() => goPrivate("dashboard.messages")} aria-current={isMobileNavActive(route, "messages") ? "page" : undefined}>
          <span className="mobile-nav-icon">
            <Icon name="message" />
            {unreadMessageCount ? <span className="mobile-nav-badge">{badgeCount(unreadMessageCount)}</span> : null}
          </span>
          <span>Сообщения</span>
        </button>
        <button className={isMobileNavActive(route, "profile") ? "active" : ""} type="button" onClick={() => goPrivate("dashboard.settings")} aria-current={isMobileNavActive(route, "profile") ? "page" : undefined}>
          <span className="mobile-nav-icon"><Icon name="user" /></span>
          <span>Профиль</span>
        </button>
      </nav>
    </>
  );
}
